import axios from 'axios';

/**
 * Servicio específico para constancia de retenciones ISR PDF
 * Centraliza todas las operaciones relacionadas con la generación de PDFs de constancias
 */

class ConstanciaRetencionesPDFApi {
    constructor() {
        this.baseURL = (process.env.REACT_APP_SERVER || '').replace(/\/+$/, '');
        this.apiBaseURL = /\/v1\/api$/i.test(this.baseURL)
            ? this.baseURL
            : `${this.baseURL}/v1/api`;
    }

    /**
     * Obtiene preview de conceptos disponibles para personalizar
     * @param {number} odoo_empleado_id - ID del empleado
     * @param {number} anio - Año fiscal
     * @returns {Promise<Object>} - Datos de preview con conceptos y totales
     */
    async obtenerPreview(odoo_empleado_id, anio) {
        try {
            const token = localStorage.getItem('token');
            
            const params = new URLSearchParams();
            params.append('odoo_empleado_id', odoo_empleado_id);
            params.append('anio', anio);

            const response = await axios.get(
                `${this.apiBaseURL}/pdf/constancia-retenciones/preview?${params.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-access-token': '1'
                    }
                }
            );

            if (!response || !response.data) {
                throw new Error('No se recibió respuesta del servidor');
            }

            return response.data;
        } catch (error) {
            console.error('Error en obtenerPreview:', error);
            throw error;
        }
    }

    /**
     * Genera un PDF de constancia con configuración personalizada
     * @param {Object} config - Configuración personalizada
     * @param {number} config.odoo_empleado_id - ID del empleado
     * @param {number} config.anio - Año fiscal
     * @param {Array<Object>} config.conceptos - Conceptos seleccionados
     * @returns {Promise<void>} - Descarga el PDF
     */
    async generarPDFPersonalizado(config) {
        try {
            const token = localStorage.getItem('token');
            
            const response = await axios.post(
                `${this.apiBaseURL}/pdf/constancia-retenciones/generar-personalizado`,
                config,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-access-token': '1',
                        'Content-Type': 'application/json'
                    },
                    responseType: 'blob'
                }
            );

            if (!response || !response.data) {
                throw new Error('No se recibió respuesta del servidor');
            }

            // Extraer nombre del archivo del header Content-Disposition
            const filename = this._extraerNombreArchivo(response.headers['content-disposition']);
            this._descargarArchivo(response.data, filename || 'constancia_retenciones.pdf');
        } catch (error) {
            console.error('Error en generarPDFPersonalizado:', error);
            throw error;
        }
    }

    /**
     * Genera un PDF de constancia de retenciones ISR
     * @param {number} odoo_empleado_id - ID del empleado
     * @param {number} anio - Año fiscal
     * @param {Array<number>} concepto_ids - IDs de conceptos a incluir (opcional)
     * @returns {Promise<Blob>} - Archivo PDF como blob
     */
    async generarPDF(odoo_empleado_id, anio, concepto_ids = []) {
        try {
            const token = localStorage.getItem('token');
            
            // Construir query params
            const params = new URLSearchParams();
            params.append('odoo_empleado_id', odoo_empleado_id);
            params.append('anio', anio);
            if (concepto_ids && concepto_ids.length > 0) {
                concepto_ids.forEach(id => params.append('concepto_ids', id));
            }

            const response = await axios.get(
                `${this.apiBaseURL}/pdf/constancia-retenciones/generar?${params.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-access-token': '1'
                    },
                    responseType: 'blob'
                }
            );

            // Verificar que la respuesta sea válida
            if (!response || !response.data) {
                throw new Error('No se recibió respuesta del servidor');
            }

            return response.data;
        } catch (error) {
            console.error('Error en generarPDF:', error);
            throw error;
        }
    }

    /**
     * Genera y descarga PDF de constancia directamente
     * @param {number} odoo_empleado_id - ID del empleado
     * @param {number} anio - Año fiscal
     * @returns {Promise<void>}
     */
    async generarYDescargarPDF(odoo_empleado_id, anio) {
        try {
            const token = localStorage.getItem('token');
            
            const params = new URLSearchParams();
            params.append('odoo_empleado_id', odoo_empleado_id);
            params.append('anio', anio);

            const response = await axios.get(
                `${this.apiBaseURL}/pdf/constancia-retenciones/generar?${params.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-access-token': '1'
                    },
                    responseType: 'blob'
                }
            );

            if (!response || !response.data) {
                throw new Error('No se recibió respuesta del servidor');
            }

            // Extraer nombre del archivo del header Content-Disposition
            const filename = this._extraerNombreArchivo(response.headers['content-disposition']);
            this._descargarArchivo(response.data, filename || 'constancia_retenciones.pdf');
        } catch (error) {
            console.error('Error en generarYDescargarPDF:', error);
            throw error;
        }
    }

    /**
     * Helper privado para extraer nombre del archivo del header Content-Disposition
     * @private
     */
    _extraerNombreArchivo(contentDisposition) {
        if (!contentDisposition) {
            return null;
        }

        // Extraer el nombre del archivo del header Content-Disposition
        // Formato: attachment; filename="nombre_archivo.pdf"
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        return filenameMatch ? filenameMatch[1] : null;
    }

    /**
     * Helper privado para descargar archivos
     * @private
     */
    _descargarArchivo(blob, nombreArchivo) {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', nombreArchivo);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}

// Exportar instancia singleton
const instance = new ConstanciaRetencionesPDFApi();
export default instance;
